import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  Calendar,
  CheckCircle2,
  Clock,
  Filter,
  Loader2,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Users,
  FileText,
  MessageSquare,
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { useToast, useConfirm } from '../../Components/Feedback';
import { Card, Select, Input, Button, Modal, Badge } from '../../Components/ui';
import { useApi } from '../../api/useApi';

const formatarDataBr = (dataIso) => {
  if (!dataIso) return '';
  const [ano, mes, dia] = dataIso.split('-');
  return `${dia}/${mes}/${ano}`;
};

const getHojeLocal = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const date = new Date(d.getTime() - offset * 60 * 1000);
  return date.toISOString().split('T')[0];
};

const ehTeoria = (nome) => Boolean(nome && /teoria/i.test(nome));

export default function Planejamento() {
  const { institutionId } = useInstitution();
  const { token, user } = useAuth();
  const { podeCriar, podeEditar, podeExcluir } = usePermissao('/planejamento');
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const api = useApi();

  const ehCoordOuMaster = ['master', 'coordenador'].includes(user?.perfil);

  // Filtros
  const [educadorFiltro, setEducadorFiltro] = useState('');
  const [disciplinaFiltro, setDisciplinaFiltro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('todos');
  const [buscaTexto, setBuscaTexto] = useState('');

  // Modais de Criação / Edição
  const [modalAberto, setModalAberto] = useState(false);
  const [planejamentoEmEdicao, setPlanejamentoEmEdicao] = useState(null);

  // Form State
  const [formDisciplina, setFormDisciplina] = useState('');
  const [formEducador, setFormEducador] = useState('');
  const [formData, setFormData] = useState(getHojeLocal());
  const [formConteudo, setFormConteudo] = useState('');
  const [formObservacoes, setFormObservacoes] = useState('');
  const [formStatus, setFormStatus] = useState('pendente');
  const [salvando, setSalvando] = useState(false);

  // Modal rápido de anotações
  const [modalObsAberto, setModalObsAberto] = useState(false);
  const [itemParaObs, setItemParaObs] = useState(null);
  const [textoObs, setTextoObs] = useState('');
  const [salvandoObs, setSalvandoObs] = useState(false);

  // 1. Carregar Disciplinas disponíveis
  const { data: disciplinas = [], isLoading: carregandoDisciplinas } = useQuery({
    queryKey: ['planejamento-disciplinas', institutionId, educadorFiltro],
    queryFn: async () => {
      const url = educadorFiltro
        ? `/api/planejamento/disciplinas?id_professor=${educadorFiltro}`
        : `/api/planejamento/disciplinas`;
      const res = await api.get(url);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token,
  });

  // 2. Carregar Educadores (para master/coordenador filtrarem)
  const { data: educadores = [] } = useQuery({
    queryKey: ['planejamento-educadores', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/planejamento/educadores`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token && ehCoordOuMaster,
  });

  // 3. Carregar Planejamentos
  const { data: planejamentos = [], isLoading: carregandoPlanejamentos } = useQuery({
    queryKey: ['planejamento-lista', institutionId, disciplinaFiltro, educadorFiltro, statusFiltro],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (disciplinaFiltro) params.set('nome_disciplina', disciplinaFiltro);
      if (educadorFiltro) params.set('id_professor', educadorFiltro);
      if (statusFiltro && statusFiltro !== 'todos') params.set('status', statusFiltro);

      const res = await api.get(`/api/planejamento?${params.toString()}`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token,
  });

  // Opções para o select de disciplinas
  const opcoesDisciplinas = useMemo(() => {
    return [
      { value: '', label: 'Todas as disciplinas' },
      ...disciplinas.map((d) => ({
        value: d.nome,
        label: d.eh_compartilhado ? `${d.nome} (Compartilhado)` : `${d.nome} - ${d.area}`,
      })),
    ];
  }, [disciplinas]);

  // Opções para o select de educadores
  const opcoesEducadores = useMemo(() => {
    return [
      { value: '', label: 'Todos os educadores' },
      ...educadores.map((e) => ({
        value: String(e.id),
        label: e.nome,
      })),
    ];
  }, [educadores]);

  // Disciplina atualmente selecionada
  const infoDisciplinaSelecionada = useMemo(() => {
    if (!disciplinaFiltro) return null;
    return disciplinas.find((d) => d.nome.toLowerCase() === disciplinaFiltro.toLowerCase());
  }, [disciplinas, disciplinaFiltro]);

  // Filtragem local por texto
  const planejamentosFiltrados = useMemo(() => {
    if (!buscaTexto.trim()) return planejamentos;
    const termo = buscaTexto.toLowerCase();
    return planejamentos.filter((p) => {
      const cont = (p.conteudo || '').toLowerCase();
      const obs = (p.observacoes || '').toLowerCase();
      const disc = (p.nome_disciplina || '').toLowerCase();
      const prof = (p.professor_nome || '').toLowerCase();
      return cont.includes(termo) || obs.includes(termo) || disc.includes(termo) || prof.includes(termo);
    });
  }, [planejamentos, buscaTexto]);

  // Abrir Modal de Criação
  const abrirNovoPlanejamento = () => {
    setPlanejamentoEmEdicao(null);
    const discPadrao = disciplinaFiltro || (disciplinas[0]?.nome ?? '');
    setFormDisciplina(discPadrao);
    const discObj = disciplinas.find((d) => d.nome === discPadrao);
    if (discObj?.eh_compartilhado || ehTeoria(discPadrao)) {
      setFormEducador('');
    } else {
      setFormEducador(educadorFiltro || (discObj?.id_professor ? String(discObj.id_professor) : ''));
    }
    setFormData(getHojeLocal());
    setFormConteudo('');
    setFormObservacoes('');
    setFormStatus('pendente');
    setModalAberto(true);
  };

  // Abrir Modal de Edição
  const abrirEdicao = (plan) => {
    setPlanejamentoEmEdicao(plan);
    setFormDisciplina(plan.nome_disciplina);
    setFormEducador(plan.id_professor ? String(plan.id_professor) : '');
    setFormData(plan.data_planejamento || plan.data_inicio_semana);
    setFormConteudo(plan.conteudo || '');
    setFormObservacoes(plan.observacoes || '');
    setFormStatus(plan.status || 'pendente');
    setModalAberto(true);
  };

  const handleMudarDisciplina = (novoNome) => {
    setFormDisciplina(novoNome);
    const discObj = disciplinas.find((d) => d.nome === novoNome);
    if (discObj?.eh_compartilhado || ehTeoria(novoNome)) {
      setFormEducador('');
    } else if (discObj?.id_professor) {
      setFormEducador(String(discObj.id_professor));
    }
  };

  // Salvar Planejamento
  const handleSalvar = async (e) => {
    e?.preventDefault();
    if (!formDisciplina) {
      toast.error('Selecione a disciplina.');
      return;
    }
    if (!formConteudo.trim()) {
      toast.error('Informe o conteúdo planejado.');
      return;
    }

    const discObj = disciplinas.find((d) => d.nome === formDisciplina);
    const ehComp = discObj?.eh_compartilhado || ehTeoria(formDisciplina);
    const profIdFinal = ehComp
      ? null
      : formEducador
        ? parseInt(formEducador, 10)
        : (discObj?.id_professor || null);

    setSalvando(true);
    try {
      if (planejamentoEmEdicao) {
        await api.patch(`/api/planejamento/${planejamentoEmEdicao.id}`, {
          conteudo: formConteudo,
          observacoes: formObservacoes,
          status: formStatus,
          data: formData,
          id_professor: profIdFinal,
        });
        toast.success('Planejamento atualizado com sucesso!');
      } else {
        await api.post('/api/planejamento', {
          nome_disciplina: formDisciplina,
          data: formData,
          conteudo: formConteudo,
          observacoes: formObservacoes,
          status: formStatus,
          id_professor: profIdFinal,
        });
        toast.success('Planejamento semanal registrado com sucesso!');
      }

      setModalAberto(false);
      queryClient.invalidateQueries({ queryKey: ['planejamento-lista'] });
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar planejamento.');
    } finally {
      setSalvando(false);
    }
  };

  // Alternar Status rápido (Pendente <-> Concluído)
  const handleToggleStatus = async (plan) => {
    const novoStatus = plan.status === 'concluido' ? 'pendente' : 'concluido';
    try {
      await api.patch(`/api/planejamento/${plan.id}`, { status: novoStatus });
      toast.success(
        novoStatus === 'concluido'
          ? 'Planejamento marcado como concluído!'
          : 'Planejamento reaberto como pendente.',
      );
      queryClient.invalidateQueries({ queryKey: ['planejamento-lista'] });
    } catch (err) {
      toast.error(err.message || 'Erro ao atualizar status.');
    }
  };

  // Abrir Modal de Observação Rápida
  const abrirModalObservacao = (plan) => {
    setItemParaObs(plan);
    setTextoObs(plan.observacoes || '');
    setModalObsAberto(true);
  };

  // Salvar Observação Rápida
  const handleSalvarObservacao = async () => {
    if (!itemParaObs) return;
    setSalvandoObs(true);
    try {
      await api.patch(`/api/planejamento/${itemParaObs.id}`, {
        observacoes: textoObs,
      });
      toast.success('Anotações salvas com sucesso!');
      setModalObsAberto(false);
      queryClient.invalidateQueries({ queryKey: ['planejamento-lista'] });
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar anotações.');
    } finally {
      setSalvandoObs(false);
    }
  };

  // Excluir Planejamento
  const handleExcluir = async (plan) => {
    const ok = await confirm({
      title: 'Excluir Planejamento Semanal',
      message: `Tem certeza que deseja remover o planejamento da semana de ${formatarDataBr(plan.data_inicio_semana)} da disciplina "${plan.nome_disciplina}"?`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await api.delete(`/api/planejamento/${plan.id}`);
      toast.success('Planejamento excluído com sucesso.');
      queryClient.invalidateQueries({ queryKey: ['planejamento-lista'] });
    } catch (err) {
      toast.error(err.message || 'Erro ao excluir planejamento.');
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
              Planejamento Semanal
            </h1>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
              <Sparkles className="w-3.5 h-3.5" /> Acompanhamento Pedagógico
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Planejamento de conteúdos por semana letiva com suporte a conteúdos compartilhados de Teoria
          </p>
        </div>

        {podeCriar && (
          <Button
            variant="primary"
            icon={Plus}
            onClick={abrirNovoPlanejamento}
            className="shadow-sm"
          >
            Novo Planejamento
          </Button>
        )}
      </div>

      {/* Barra de Filtros */}
      <Card className="p-4 sm:p-5 border border-gray-100 dark:border-gray-800 shadow-sm bg-white dark:bg-gray-900">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ehCoordOuMaster && (
            <Select
              label="Filtrar por Educador"
              icon={Users}
              value={educadorFiltro}
              onChange={(e) => {
                setEducadorFiltro(e.target.value);
                setDisciplinaFiltro('');
              }}
              options={opcoesEducadores}
            />
          )}

          <Select
            label="Disciplina"
            icon={BookOpen}
            value={disciplinaFiltro}
            onChange={(e) => setDisciplinaFiltro(e.target.value)}
            options={opcoesDisciplinas}
          />

          <Select
            label="Status"
            icon={Filter}
            value={statusFiltro}
            onChange={(e) => setStatusFiltro(e.target.value)}
            options={[
              { value: 'todos', label: 'Todos os status' },
              { value: 'pendente', label: 'Apenas Pendentes' },
              { value: 'concluido', label: 'Apenas Concluídos' },
            ]}
          />

          <Input
            label="Buscar conteúdo ou anotação"
            icon={Search}
            placeholder="Digite para filtrar..."
            value={buscaTexto}
            onChange={(e) => setBuscaTexto(e.target.value)}
          />
        </div>

        {/* Alerta de Teoria Compartilhada */}
        {infoDisciplinaSelecionada?.eh_compartilhado && (
          <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-xl flex items-start gap-2.5 text-xs text-purple-800 dark:text-purple-300">
            <Users className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold">Conteúdo Compartilhado:</span> Esta disciplina de Teoria é
              compartilhada entre todos os educadores que a lecionam. Qualquer alteração ou conclusão
              feita aqui é sincronizada em tempo real para todos os professores da disciplina.
            </div>
          </div>
        )}
      </Card>

      {/* Lista de Planejamentos */}
      <div className="space-y-4">
        {carregandoPlanejamentos || carregandoDisciplinas ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600 dark:text-blue-400 mb-2" />
            Carregando planejamentos semanais...
          </div>
        ) : planejamentosFiltrados.length === 0 ? (
          <Card className="p-12 text-center border-dashed border-2 border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <BookOpen className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Nenhum planejamento encontrado
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
              {disciplinaFiltro
                ? `Ainda não há planejamentos registrados para ${disciplinaFiltro}. Clique em "Novo Planejamento" para cadastrar a primeira semana.`
                : 'Selecione uma disciplina ou clique em "Novo Planejamento" para começar.'}
            </p>
            {podeCriar && (
              <Button
                variant="primary"
                icon={Plus}
                onClick={abrirNovoPlanejamento}
                className="mt-4"
              >
                Cadastrar Primeiro Planejamento
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {planejamentosFiltrados.map((plan) => {
              const isConcluido = plan.status === 'concluido';

              return (
                <Card
                  key={plan.id}
                  className={`p-5 border transition-all shadow-sm ${
                    isConcluido
                      ? 'border-green-200 dark:border-green-900/40 bg-white dark:bg-gray-900'
                      : 'border-amber-200 dark:border-amber-900/40 bg-white dark:bg-gray-900'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    {/* Header do Card */}
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-base text-gray-900 dark:text-gray-100">
                          {plan.nome_disciplina}
                        </span>

                        {plan.eh_compartilhado ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                            <Users className="w-3 h-3" /> Teoria Compartilhada
                          </span>
                        ) : plan.professor_nome ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                            Educador: {plan.professor_nome}
                          </span>
                        ) : null}

                        <Badge tone={isConcluido ? 'success' : 'warning'}>
                          {isConcluido ? (
                            <span className="inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Concluído
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" /> Pendente
                            </span>
                          )}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        <span>
                          Semana {plan.numero_semana || ''} • {formatarDataBr(plan.data_inicio_semana)} a{' '}
                          {formatarDataBr(plan.data_fim_semana)}
                        </span>
                      </div>
                    </div>

                    {/* Ações do Card */}
                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                      {podeEditar && (
                        <Button
                          variant={isConcluido ? 'secondary' : 'success'}
                          size="sm"
                          icon={isConcluido ? Clock : CheckCircle2}
                          onClick={() => handleToggleStatus(plan)}
                          title={isConcluido ? 'Reabrir como pendente' : 'Marcar como concluído'}
                        >
                          {isConcluido ? 'Reabrir' : 'Concluir'}
                        </Button>
                      )}

                      <Button
                        variant="secondary"
                        size="sm"
                        icon={MessageSquare}
                        onClick={() => abrirModalObservacao(plan)}
                        title="Acrescentar anotações da aula"
                      >
                        {plan.observacoes ? 'Ver Anotações' : '+ Anotação'}
                      </Button>

                      {podeEditar && (
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={Pencil}
                          onClick={() => abrirEdicao(plan)}
                          title="Editar conteúdo"
                        >
                          Editar
                        </Button>
                      )}

                      {podeExcluir && (
                        <Button
                          variant="danger"
                          size="sm"
                          icon={Trash2}
                          onClick={() => handleExcluir(plan)}
                          title="Excluir planejamento"
                        />
                      )}
                    </div>
                  </div>

                  {/* Conteúdo Planejado */}
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" /> Conteúdo Planejado
                    </div>
                    <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed bg-gray-50/50 dark:bg-gray-800/40 p-3 rounded-xl border border-gray-100/80 dark:border-gray-800">
                      {plan.conteudo}
                    </div>
                  </div>

                  {/* Observações / O que foi dado */}
                  {plan.observacoes && (
                    <div className="mt-3">
                      <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                        <MessageSquare className="w-3.5 h-3.5" /> Anotações / Realizado na Semana
                      </div>
                      <div className="text-sm text-blue-900 dark:text-blue-200 whitespace-pre-wrap leading-relaxed bg-blue-50/60 dark:bg-blue-950/40 p-3 rounded-xl border border-blue-100 dark:border-blue-900/40">
                        {plan.observacoes}
                      </div>
                    </div>
                  )}

                  {/* Rodapé de Auditoria */}
                  <div className="mt-4 pt-2 text-[11px] text-gray-400 dark:text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {plan.criador_nome && (
                      <span>Registrado por: <strong>{plan.criador_nome}</strong></span>
                    )}
                    {plan.atualizador_nome && (
                      <span>• Última alteração por: <strong>{plan.atualizador_nome}</strong></span>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de Criação / Edição */}
      <Modal
        isOpen={modalAberto}
        onClose={() => !salvando && setModalAberto(false)}
        title={planejamentoEmEdicao ? 'Editar Planejamento Semanal' : 'Novo Planejamento Semanal'}
        maxWidth="lg"
      >
        <form onSubmit={handleSalvar} className="p-4 sm:p-6 space-y-4">
          <Select
            label="Disciplina"
            icon={BookOpen}
            value={formDisciplina}
            onChange={(e) => handleMudarDisciplina(e.target.value)}
            disabled={!!planejamentoEmEdicao}
            options={disciplinas.map((d) => ({
              value: d.nome,
              label: d.eh_compartilhado
                ? `${d.nome} (Teoria Compartilhada)`
                : d.professor_nome
                  ? `${d.nome} - ${d.area} (${d.professor_nome})`
                  : `${d.nome} - ${d.area}`,
            }))}
          />

          {ehCoordOuMaster && !ehTeoria(formDisciplina) && (
            <Select
              label="Educador Responsável"
              icon={Users}
              value={formEducador}
              onChange={(e) => setFormEducador(e.target.value)}
              options={[
                { value: '', label: 'Geral (Todos os educadores desta disciplina)' },
                ...educadores.map((e) => ({
                  value: String(e.id),
                  label: e.nome,
                })),
              ]}
            />
          )}

          {ehTeoria(formDisciplina) && (
            <div className="p-3 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-xl flex items-center gap-2 text-xs text-purple-800 dark:text-purple-300">
              <Users className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
              <span>
                <strong>Teoria Compartilhada:</strong> Este conteúdo será compartilhado automaticamente com todos os educadores que ministram esta teoria.
              </span>
            </div>
          )}

          <Input
            label="Data de Referência (determina a semana letiva)"
            type="date"
            icon={Calendar}
            value={formData}
            onChange={(e) => setFormData(e.target.value)}
          />

          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Conteúdo Planejado *
            </label>
            <textarea
              rows={5}
              required
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Descreva detalhadamente os tópicos, exercícios e objetivos da aula para esta semana..."
              value={formConteudo}
              onChange={(e) => setFormConteudo(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Anotações / Realizado (opcional - pode preencher após a aula)
            </label>
            <textarea
              rows={3}
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Observações sobre o andamento da turma, o que foi concluído ou pendências..."
              value={formObservacoes}
              onChange={(e) => setFormObservacoes(e.target.value)}
            />
          </div>

          <Select
            label="Status"
            value={formStatus}
            onChange={(e) => setFormStatus(e.target.value)}
            options={[
              { value: 'pendente', label: 'Pendente (Em andamento)' },
              { value: 'concluido', label: 'Concluído (Feito)' },
            ]}
          />

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setModalAberto(false)}
              disabled={salvando}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={salvando}
              icon={salvando ? Loader2 : CheckCircle2}
            >
              {salvando ? 'Salvando...' : 'Salvar Planejamento'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de Anotações Rápidas */}
      <Modal
        isOpen={modalObsAberto}
        onClose={() => !salvandoObs && setModalObsAberto(false)}
        title={`Anotações: ${itemParaObs?.nome_disciplina || ''}`}
        maxWidth="md"
      >
        <div className="p-4 sm:p-6 space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Acrescente observações, registros pós-aula ou detalhes sobre o que foi ministrado nesta semana:
          </p>

          <textarea
            rows={5}
            className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Turma conseguiu avançar até o exercício 5. Na próxima semana retomar figuras..."
            value={textoObs}
            onChange={(e) => setTextoObs(e.target.value)}
          />

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setModalObsAberto(false)}
              disabled={salvandoObs}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={salvandoObs}
              onClick={handleSalvarObservacao}
              icon={salvandoObs ? Loader2 : CheckCircle2}
            >
              {salvandoObs ? 'Salvando...' : 'Salvar Anotações'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

